/**
 * Repository Mapping Utility
 * 
 * Maps table names (strings) to repository instances for use in dataSourceService.
 * This allows dataSourceService to route Dexie operations to the correct repositories.
 */

import { BaseRepository } from './BaseRepository';
import type { BaseEntity } from './BaseRepository';
import {
  certificateRepository,
  equipmentRepository,
  workOrderRepository,
  maintenanceRecordRepository,
  personnelRepository,
  crewCertificationRepository,
  workingHoursRepository,
  trainingRepository,
  trainingRecordRepository,
  incidentRepository,
  drillRepository,
  inspectionRepository,
  riskAssessmentRepository,
  inventoryItemRepository,
  stockTransactionRepository,
  requisitionRepository,
  documentRepository,
  oilRecordRepository,
  garbageRecordRepository,
  ballastWaterRepository,
  emissionRecordRepository,
  positionRepository,
  passageRepository,
  waypointRepository,
  portCallRepository,
  auditRepository,
  nonConformityRepository,
  complianceCheckRepository,
  userRepository,
  notificationRepository,
  kpiRepository,
  performanceMetricRepository,
  syncQueueRepository,
  formRepository,
  formSubmissionRepository,
  correctiveActionRepository,
  logbookRepository,
  logbookTemplateRepository,
  watchPeriodRepository,
} from './repositories';

/**
 * Map of table names to repository instances
 * 
 * Table names match Supabase table names (snake_case) and Dexie table names (camelCase)
 */
const TABLE_TO_REPOSITORY: Record<string, BaseRepository<BaseEntity>> = {
  // Certificates
  'certificates': certificateRepository as BaseRepository<BaseEntity>,
  
  // Maintenance
  'equipment': equipmentRepository as BaseRepository<BaseEntity>,
  'maintenance_records': maintenanceRecordRepository as BaseRepository<BaseEntity>,
  'work_orders': workOrderRepository as BaseRepository<BaseEntity>,
  'workOrders': workOrderRepository as BaseRepository<BaseEntity>,
  
  // Crew
  'personnel': personnelRepository as BaseRepository<BaseEntity>,
  'crew_members': personnelRepository as BaseRepository<BaseEntity>,
  'crew_certifications': crewCertificationRepository as BaseRepository<BaseEntity>,
  'working_hours': workingHoursRepository as BaseRepository<BaseEntity>,
  'trainings': trainingRepository as BaseRepository<BaseEntity>,
  'training_records': trainingRecordRepository as BaseRepository<BaseEntity>,
  
  // Safety
  'incidents': incidentRepository as BaseRepository<BaseEntity>,
  'drills': drillRepository as BaseRepository<BaseEntity>,
  'inspections': inspectionRepository as BaseRepository<BaseEntity>,
  'risk_assessments': riskAssessmentRepository as BaseRepository<BaseEntity>,
  
  // Inventory
  'inventory_items': inventoryItemRepository as BaseRepository<BaseEntity>,
  'stock_transactions': stockTransactionRepository as BaseRepository<BaseEntity>,
  'requisitions': requisitionRepository as BaseRepository<BaseEntity>,
  
  // Documents
  'documents': documentRepository as BaseRepository<BaseEntity>,
  
  // Environmental
  'oil_records': oilRecordRepository as BaseRepository<BaseEntity>,
  'oil_record_book': oilRecordRepository as BaseRepository<BaseEntity>,
  'garbage_records': garbageRecordRepository as BaseRepository<BaseEntity>,
  'garbage_record_book': garbageRecordRepository as BaseRepository<BaseEntity>,
  'ballast_water': ballastWaterRepository as BaseRepository<BaseEntity>,
  'ballast_records': ballastWaterRepository as BaseRepository<BaseEntity>,
  'emission_records': emissionRecordRepository as BaseRepository<BaseEntity>,
  
  // Navigation
  'positions': positionRepository as BaseRepository<BaseEntity>,
  'passages': passageRepository as BaseRepository<BaseEntity>,
  'waypoints': waypointRepository as BaseRepository<BaseEntity>,
  'port_calls': portCallRepository as BaseRepository<BaseEntity>,
  
  // Compliance
  'audits': auditRepository as BaseRepository<BaseEntity>,
  'non_conformities': nonConformityRepository as BaseRepository<BaseEntity>,
  'compliance_checks': complianceCheckRepository as BaseRepository<BaseEntity>,
  
  // System
  'users': userRepository as BaseRepository<BaseEntity>,
  'notifications': notificationRepository as BaseRepository<BaseEntity>,
  'kpis': kpiRepository as BaseRepository<BaseEntity>,
  'performance_metrics': performanceMetricRepository as BaseRepository<BaseEntity>,
  'sync_queue': syncQueueRepository as BaseRepository<BaseEntity>,

  // Forms
  'forms': formRepository as BaseRepository<BaseEntity>,
  'form_submissions': formSubmissionRepository as BaseRepository<BaseEntity>,
  
  // Corrective Actions
  'corrective_actions': correctiveActionRepository as BaseRepository<BaseEntity>,
  
  // Logbook
  'logbook_entries': logbookRepository as BaseRepository<BaseEntity>,
  'logbook_templates': logbookTemplateRepository as BaseRepository<BaseEntity>,
  'logbook_watch_periods': watchPeriodRepository as BaseRepository<BaseEntity>,
};

/**
 * Get repository instance for a given table name
 * 
 * @param table - Table name (can be snake_case or camelCase)
 * @returns Repository instance or null if not found
 */
export function getRepositoryForTable(table: string): BaseRepository<BaseEntity> | null {
  const repository = TABLE_TO_REPOSITORY[table];
  if (!repository) {
    // Try camelCase version if snake_case not found
    const camelCase = table.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    return TABLE_TO_REPOSITORY[camelCase] || null;
  }
  return repository;
}

/**
 * Check if a table has a repository mapping
 */
export function hasRepositoryForTable(table: string): boolean {
  return getRepositoryForTable(table) !== null;
}

/**
 * Get all mapped table names
 */
export function getMappedTables(): string[] {
  return Object.keys(TABLE_TO_REPOSITORY);
}

